import { useCallback, useContext, useReducer, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CrudMode, Page } from '@ohpen/enso-ui';
import { FirmsList, FirmsCrudForm, FIRMS_ENTITY_LIST_NAMES } from '@pmp/components';
import { ActionEntityType, FirmGetAllResponse } from '@pmp/api';
import { useValueListsStore } from '@pmp/stores';
import { PmpConfigContext } from '@pmp/contexts';
import { usePmpFetch } from '@pmp/hooks';

import { initialState, reducer } from './reducer';

export const FirmsPage = (): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('lenderFirmsGetAllLenderFirms');
  const [state, dispatch] = useReducer(reducer, initialState);
  const { valueLists, getValueLists } = useValueListsStore();
  const { brokerPortalUrl } = useContext(PmpConfigContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    getValueLists({ alwaysFetch: FIRMS_ENTITY_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: FirmGetAllResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: FirmGetAllResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onOpenInPanel = useCallback(
    (record: FirmGetAllResponse) => {
      if (!brokerPortalUrl || !record?.ohpenId) return;
      window.open(`${brokerPortalUrl}/panel/firms/${record.ohpenId}`, '_blank');
    },
    [brokerPortalUrl],
  );

  const onSeeFirmCases = useCallback(
    (record: FirmGetAllResponse) => {
      if (!brokerPortalUrl || !record?.ohpenId) return;
      window.open(`${brokerPortalUrl}/broker/cases?firmContext=${record.ohpenId}`, '_blank');
    },
    [brokerPortalUrl],
  );

  const onManageFirmCases = useCallback(
    (record: FirmGetAllResponse) => {
      if (!brokerPortalUrl || !record?.ohpenId) return;
      window.open(`${brokerPortalUrl}/sso/auth?firmContext=${record?.ohpenId}`, '_blank');
    },
    [brokerPortalUrl],
  );

  const onSeeFirmIntermediaries = useCallback(
    (record: FirmGetAllResponse) => {
      if (!record?.name) return;
      navigate(`/brokers?search-${ActionEntityType.BROKERS}=${encodeURIComponent(record.name)}`);
    },
    [navigate],
  );

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: FirmGetAllResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  return (
    <Page>
      <Page.Title>{t('lender.firms.title')}</Page.Title>
      <Page.Content>
        <FirmsList
          isFetching={isFetching}
          data={data ?? []}
          valueLists={valueLists}
          onCreate={onCreate}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onOpenInPanel={onOpenInPanel}
          onSeeFirmCases={onSeeFirmCases}
          onManageFirmCases={onManageFirmCases}
          onSeeFirmIntermediaries={onSeeFirmIntermediaries}
        />
        {state.crud.open && state.crud.mode && (
          <FirmsCrudForm
            valueLists={valueLists}
            mode={state.crud.mode}
            record={state.crud.record}
            refreshTable={fetchData}
            onClose={onCloseCrud}
          />
        )}
      </Page.Content>
    </Page>
  );
};
